/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useState} from 'react';
import {I18nextProvider} from 'react-i18next';
import {QueryClientProvider} from 'react-query';
import {ThemeProvider} from '@material-ui/core/styles';
import PageViewportMeta from '@ion/applications/src/components/PageViewportMeta';
import {ApplicationPage} from '@ion/applications/src/layout/types';
import {NetworkStatusPrompt} from '@ion/applications/src/prompts/NetworkStatusPrompt';
import {getFeatureFlags} from '@ion/applications/src/routes/RouteUtils';
import StoreProvider from '@ion/applications/src/store/StoreProvider';
import {launchReduxDevTools} from '@ion/applications/src/store/tools';
import {isDevMode} from '@ion/applications/src/utils/env';
import Pendo from '@ion/core/src/analytics/Pendo';
import {get, loadConfig} from '@ion/core/src/environment/environment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import i18n, {initializeLanguage, localeHMR} from '@ion/core/src/locale/locale';
import {enable, info} from '@ion/core/src/logger/logger';
import {setConnectConfig} from '@ion/core/src/services/ConnectUtils';
import Sisense from '@ion/core/src/services/Sisense';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {registerGrid} from '@ion/dls/src/grid/IonGridLicence';
import {setStaticGridConfig} from '@ion/dls/src/grid/useStaticGridConfig';
import {ErrorBoundary} from '@ion/dls/src/notifications/ErrorBoundary';
import {getQueryClient} from '@ion/query/src/config/client';
import {DevTools} from '@ion/query/src/tools/DevTools';
import {setGraphQLConfig} from '@ion/services/src/graphql/GraphQLConfig';
import {initializeRouter, isDebug} from '@/routes/RouteDefinitions';
import rootReducer from '@/store/reducers';
import Layout from '../layout/Layout';
import {
  getAPMEnablement,
  initializeAPM
} from '../security/apm';
import FeatureFlags from '../security/FeatureFlags';
import theme from '../theme/theme';
import '@ion/dls/src/fonts/fonts.scss';
import '@ion/applications/src/styles/index.scss';
import 'react-phone-number-input/style.css';
import 'filepond/dist/filepond.min.css';

const isDev = isDevMode();

/**
 * Defines the root iOn Application, this component wraps all routes and injects
 * all providers, including i18Next and Material UI.
 */
function CTCAdmin({Component, pageProps}: ApplicationPage) {
  const [initialized, setInitialized]       = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const layoutProps = useMemo(() => Component.getLayoutProps?.() || {}, [Component]);

  const isMounted  = useHasMounted();

  useEffect(() => {
    FeatureFlags.applyFeatureFlags(getFeatureFlags('features'));
  }, []);

  useEffect(() => {
    setStaticGridConfig({rowBuffer: 50});
    if (isMounted && !initialized && !isInitializing) {
      setIsInitializing(true);
      const {enableAPMFramework, enableAPMDebugOnly} = getAPMEnablement();
      enable(isDebug() || isDev || enableAPMDebugOnly);
      initializeRouter();
      info('Initializing Application ...');
      initializeLanguage();
      loadConfig(config => {
        if (!isDev || (enableAPMFramework && !enableAPMDebugOnly)) {
          Pendo.injectScript(config.pendoAPIKey, globalThis);
        }
        setGraphQLConfig({
          serviceURL : get('graphQLServiceURL'),
          appKey     : get('appKey')
        });
        initializeAPM(enableAPMFramework, enableAPMDebugOnly);
        Sisense.injectFrameScript();
        setConnectConfig(config);
        setInitialized(true);
        registerGrid(config);
      }, false);
      isDev && localeHMR();
      isDev && launchReduxDevTools();
    }
  }, [isMounted, initialized, isInitializing]);

  return (
    <>
      <PageViewportMeta />
      {initialized && (
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={getQueryClient()}>
              <ErrorBoundary>
                <StoreProvider rootReducer={rootReducer}>
                  {!layoutProps.omit ? (
                    <Layout {...layoutProps}>
                      <Component {...pageProps} />
                    </Layout>
                  ) : (
                    <Component {...pageProps}/>
                  )}
                </StoreProvider>
              </ErrorBoundary>
              <DevTools />
            </QueryClientProvider>
          </ThemeProvider>
        </I18nextProvider>
      )}
      {isMounted && !initialized && (
        <ThemeProvider theme={theme}>
          <ContentLoader width='60px' height='60px' fullPage />
        </ThemeProvider>
      )}
      <NetworkStatusPrompt />
    </>
  );
};
export default CTCAdmin;
